import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretDown } from "@phosphor-icons/react";
import { useLanguage } from "../../context/LanguageContext";

const ProgramCard = ({ p, gi, pi }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-black/15 bg-white" data-testid={`program-card-${gi}-${pi}`}>
      <button
        data-testid={`program-toggle-${gi}-${pi}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex flex-wrap items-center gap-3 px-5 py-4 text-left transition-colors duration-300 hover:bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-brand-green"
      >
        <span className="font-cabinet font-bold text-base md:text-lg tracking-tight">{p.name}</span>
        {p.level && (
          <span className="bg-brand-ink text-brand-paper px-2.5 py-1 text-[10px] font-bold tracking-wider uppercase">
            {p.level}
          </span>
        )}
        <span className="bg-brand-green/10 text-brand-green px-2.5 py-1 text-[10px] font-bold tracking-wider uppercase">
          {p.units.length} {t.services.unitsLabel}
        </span>
        <CaretDown
          size={16}
          weight="bold"
          className={`ml-auto text-black/40 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-4 border-t border-black/10">
              {p.audience && (
                <p className="text-xs text-black/55 leading-relaxed mb-4">
                  <span className="font-bold tracking-wider uppercase text-black/45">
                    {t.services.audienceLabel}:{" "}
                  </span>
                  {p.audience.join(" · ")}
                </p>
              )}
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/45 mb-3">
                {t.services.unitsLabel}
              </p>
              <ol className="grid md:grid-cols-2 gap-x-8 gap-y-2.5">
                {p.units.map((u, i) => (
                  <li key={i} className="flex gap-3 text-sm text-black/70 leading-snug">
                    <span className="font-cabinet font-bold text-brand-green shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {u}
                  </li>
                ))}
              </ol>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ProgramsBlock = ({ programs }) => (
  <div className="flex flex-col gap-8" data-testid="bnsp-programs">
    {programs.map((g, gi) => (
      <div key={g.group}>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-brand-red mb-4">{g.group}</p>
        <div className="flex flex-col gap-3">
          {g.items.map((p, pi) => (
            <ProgramCard key={p.name} p={p} gi={gi} pi={pi} />
          ))}
        </div>
      </div>
    ))}
  </div>
);
